import type { SegmentLogicalOp } from '@masters-club/validation';

/**
 * Русские подписи логических операторов конструктора сегмента.
 * Контракт хранит AND/OR (англ.), пользователю показываем И/ИЛИ.
 */
const LOGICAL_OP_LABELS: Record<SegmentLogicalOp, string> = {
  AND: 'И',
  OR: 'ИЛИ',
};

export function logicalOpLabel(op: SegmentLogicalOp): string {
  return LOGICAL_OP_LABELS[op];
}

/**
 * Обобщённое представление сущности-справочника из useList. Разные
 * Refine-ресурсы хранят локализованное название по-разному:
 *  - courses/books: translations: [{ languageCode, title }] + slug
 *  - channel-passes/subscription-plans: translations: Record<lang, { name }>
 *  - languages/tags/прочее: name | title | label
 * resolveEntityLabel разбирает любой из вариантов без кастов (type guards).
 */
export interface EntityLike {
  id: number | string;
  name?: string;
  title?: string;
  label?: string;
  slug?: string;
  code?: string;
  translations?: unknown;
}

interface ArrayTranslation {
  languageCode: string;
  title?: string;
  name?: string;
}

function isArrayTranslations(v: unknown): v is ArrayTranslation[] {
  return (
    Array.isArray(v) &&
    v.every((t) => typeof t === 'object' && t !== null && 'languageCode' in t)
  );
}

function isRecordTranslations(
  v: unknown,
): v is Record<string, { name?: string; title?: string } | undefined> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Человекочитаемое название сущности: прямое name/title/label →
 * перевод (ru → первый) → slug/code → "#id" в крайнем случае.
 */
export function resolveEntityLabel(e: EntityLike): string {
  const direct = e.name ?? e.title ?? e.label;
  if (direct) {
    return direct;
  }

  const tr = e.translations;
  if (isArrayTranslations(tr)) {
    const ru = tr.find((t) => t.languageCode === 'ru');
    const pick = ru ?? tr[0];
    const name = pick?.title ?? pick?.name;
    if (name) {
      return name;
    }
  } else if (isRecordTranslations(tr)) {
    const ru = tr['ru'];
    const ruName = ru?.name ?? ru?.title;
    if (ruName) {
      return ruName;
    }
    const firstKey = Object.keys(tr)[0];
    if (firstKey) {
      const first = tr[firstKey];
      const firstName = first?.name ?? first?.title;
      if (firstName) {
        return firstName;
      }
    }
  }

  return e.slug ?? e.code ?? `#${e.id}`;
}
